import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import jobService from '../services/jobService';

/**
 * Custom hook to manage job details and actions
 * Uses React Query for data fetching, caching, and mutations
 *
 * @param {string|number} jobId - The ID of the job to fetch
 * @returns {Object} Job data, loading states, and mutation functions
 */
export function useJobDetails(jobId) {
  const queryClient = useQueryClient();

  // Fetch job details query
  const jobQuery = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => jobService.getJob(jobId),
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    staleTime: 30000, // Consider data stale after 30 seconds
    enabled: !!jobId, // Only fetch if jobId is provided
  });

  // Start job mutation
  const startJobMutation = useMutation({
    mutationFn: () => jobService.startJob(jobId),
    onSuccess: (data) => {
      // Update the job query cache with new data
      queryClient.setQueryData(['job', jobId], data);
      // Invalidate jobs list to refresh dashboard
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      console.error('Error starting job:', error);
    },
  });

  // Complete job mutation
  const completeJobMutation = useMutation({
    mutationFn: () => jobService.completeJob(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(['job', jobId], data);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      console.error('Error completing job:', error);
    },
  });

  // Cancel job mutation
  const cancelJobMutation = useMutation({
    mutationFn: () => jobService.cancelJob(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(['job', jobId], data);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      console.error('Error cancelling job:', error);
    },
  });

  // Epic 14 US-14.4: New workflow transition mutations
  
  // Confirm job mutation (PENDING → CONFIRMED)
  const confirmJobMutation = useMutation({
    mutationFn: () => jobService.confirmJob(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(['job', jobId], data);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      console.error('Error confirming job:', error);
    },
  });

  // Pause job mutation (IN_PROGRESS → PAUSED)
  const pauseJobMutation = useMutation({
    mutationFn: () => jobService.pauseJob(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(['job', jobId], data);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      console.error('Error pausing job:', error);
    },
  });

  // Resume job mutation (PAUSED → IN_PROGRESS)
  const resumeJobMutation = useMutation({
    mutationFn: () => jobService.resumeJob(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(['job', jobId], data);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      console.error('Error resuming job:', error);
    },
  });

  // Review job mutation (COMPLETED → UNDER_REVIEW)
  const reviewJobMutation = useMutation({
    mutationFn: () => jobService.reviewJob(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(['job', jobId], data);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      console.error('Error sending job to review:', error);
    },
  });

  // Approve job mutation (UNDER_REVIEW → APPROVED)
  const approveJobMutation = useMutation({
    mutationFn: () => jobService.approveJob(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(['job', jobId], data);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      console.error('Error approving job:', error);
    },
  });

  // Reject job mutation (UNDER_REVIEW → REJECTED)
  const rejectJobMutation = useMutation({
    mutationFn: () => jobService.rejectJob(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(['job', jobId], data);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      console.error('Error rejecting job:', error);
    },
  });

  // Send job mutation (APPROVED → SENT)
  const sendJobMutation = useMutation({
    mutationFn: () => jobService.sendJob(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(['job', jobId], data);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      console.error('Error sending job:', error);
    },
  });

  // Archive job mutation (SENT → ARCHIVED)
  const archiveJobMutation = useMutation({
    mutationFn: () => jobService.archiveJob(jobId),
    onSuccess: (data) => {
      queryClient.setQueryData(['job', jobId], data);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      console.error('Error archiving job:', error);
    },
  });

  return {
    // Job data and loading states
    job: jobQuery.data,
    isLoading: jobQuery.isLoading,
    isError: jobQuery.isError,
    error: jobQuery.error,
    refetch: jobQuery.refetch,

    // Original mutation functions
    startJob: startJobMutation,
    completeJob: completeJobMutation,
    cancelJob: cancelJobMutation,

    // Epic 14 US-14.4: New workflow transition functions
    confirmJob: confirmJobMutation,
    pauseJob: pauseJobMutation,
    resumeJob: resumeJobMutation,
    reviewJob: reviewJobMutation,
    approveJob: approveJobMutation,
    rejectJob: rejectJobMutation,
    sendJob: sendJobMutation,
    archiveJob: archiveJobMutation,

    // Helper flags for UI
    isStarting: startJobMutation.isPending,
    isCompleting: completeJobMutation.isPending,
    isCancelling: cancelJobMutation.isPending,
    isConfirming: confirmJobMutation.isPending,
    isPausing: pauseJobMutation.isPending,
    isResuming: resumeJobMutation.isPending,
    isReviewing: reviewJobMutation.isPending,
    isApproving: approveJobMutation.isPending,
    isRejecting: rejectJobMutation.isPending,
    isSending: sendJobMutation.isPending,
    isArchiving: archiveJobMutation.isPending,
  };
}

export default useJobDetails;
